import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { frontendLogger } from '../../../shared/logger';

export class CharacterLoader {
  private static instance: CharacterLoader;
  private gltfLoader: GLTFLoader;
  private characterCache: Map<string, THREE.Group> = new Map();
  
  private constructor() {
    this.gltfLoader = new GLTFLoader();
  }
  
  static getInstance(): CharacterLoader {
    if (!CharacterLoader.instance) {
      CharacterLoader.instance = new CharacterLoader();
    }
    return CharacterLoader.instance;
  }
  
  /**
   * Load a blocky character model (like Kenney's Blocky Characters)
   * Falls back to procedural generation if file doesn't exist
   */
  async loadCharacter(
    filename: string,
    fallbackColor: number = 0x4a90e2
  ): Promise<THREE.Group> {
    // Check cache
    if (this.characterCache.has(filename)) {
      return this.characterCache.get(filename)!.clone();
    }
    
    try {
      // Try to load GLB model
      const character = await this.loadGLB(filename);
      this.characterCache.set(filename, character);
      return character.clone();
    } catch (error) {
      frontendLogger.warn(`Character model not found: ${filename}, using procedural fallback`);
      return this.createProceduralCharacter(fallbackColor);
    }
  }
  
  private loadGLB(filename: string): Promise<THREE.Group> {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        `/models/${filename}`,
        (gltf: any) => {
          const character = gltf.scene;
          
          // Setup for voxel/blocky style
          character.traverse((child: any) => {
            if (child instanceof THREE.Mesh) {
              // Enable shadows
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Make materials look more blocky
              if (child.material instanceof THREE.MeshStandardMaterial) {
                child.material.roughness = 0.8;
                child.material.metalness = 0.1;
              }
            }
          });
          
          // Standard character size
          const box = new THREE.Box3().setFromObject(character);
          const size = box.getSize(new THREE.Vector3());
          const targetHeight = 1.8; // Standard player height
          const scale = targetHeight / size.y;
          character.scale.multiplyScalar(scale);
          
          frontendLogger.info(`Loaded character: ${filename}`);
          resolve(character);
        },
        undefined,
        (error: any) => {
          reject(error);
        }
      );
    });
  }
  
  /**
   * Fallback: Create a simple blocky character procedurally
   * Similar to Minecraft/Koxels.io style
   */
  private createProceduralCharacter(color: number): THREE.Group {
    const group = new THREE.Group();
    
    const material = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.8,
      metalness: 0.1
    });
    
    // Body
    const bodyGeometry = new THREE.BoxGeometry(0.6, 0.8, 0.3);
    const body = new THREE.Mesh(bodyGeometry, material);
    body.position.y = 0.8;
    body.castShadow = true;
    group.add(body);
    
    // Head
    const headGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.4);
    const head = new THREE.Mesh(headGeometry, material);
    head.position.y = 1.4;
    head.castShadow = true;
    group.add(head);
    
    // Arms
    const armGeometry = new THREE.BoxGeometry(0.15, 0.6, 0.15);
    const leftArm = new THREE.Mesh(armGeometry, material);
    leftArm.position.set(-0.4, 0.8, 0);
    leftArm.castShadow = true;
    group.add(leftArm);
    
    const rightArm = new THREE.Mesh(armGeometry, material);
    rightArm.position.set(0.4, 0.8, 0);
    rightArm.castShadow = true;
    group.add(rightArm);
    
    // Legs
    const legGeometry = new THREE.BoxGeometry(0.2, 0.6, 0.2);
    const leftLeg = new THREE.Mesh(legGeometry, material);
    leftLeg.position.set(-0.15, 0.3, 0);
    leftLeg.castShadow = true;
    group.add(leftLeg);
    
    const rightLeg = new THREE.Mesh(legGeometry, material);
    rightLeg.position.set(0.15, 0.3, 0);
    rightLeg.castShadow = true;
    group.add(rightLeg);
    
    return group;
  }
  
  /**
   * Load Kenney's Blocky Characters specifically
   * Download from: https://kenney.nl/assets/blocky-characters
   */
  async loadKenneyCharacter(
    characterName: string = 'character_male'
  ): Promise<THREE.Group> {
    // Kenney's characters are usually named like:
    // character_male.glb, character_female.glb, character_zombie.glb, etc.
    return this.loadCharacter(`${characterName}.glb`, 0x4a90e2);
  }
}
